import { FormDefinition, FormStepDefinition } from '../api/interfaces'
import { Ceo } from './steps/Ceo'
import { TaxNumber } from './steps/TaxNumber'
import { useAppSelector } from '../../app/hooks'
import { useGetLeadByTokenQuery, usePrefetch, useUpdateLeadMutation } from '../api/apiSlice'
import { skipToken } from '@reduxjs/toolkit/query'
import { Alert, CircularProgress } from '@mui/material'
import React from 'react'
import { useNavigate } from 'react-router-dom'
import { CompanyAddress } from './steps/CompanyAddress'
import { BankAccount } from './steps/BankAccount'
import { CompanyContact } from './steps/CompanyContact'
import { Agreements } from './steps/Agreements'
import { IataAndCrs } from './steps/IataAndCrs'
import { Chain } from './steps/Chain'
import { Comment } from './steps/Comment'

interface FormStepRendererProps {
    formStepDefinition: FormStepDefinition,
    form: FormDefinition
}

export const FormStepRenderer = ({ formStepDefinition, form }: FormStepRendererProps) => {
    const token = useAppSelector((state) => state.form.token)
    const { data: lead, error: leadError } = useGetLeadByTokenQuery(token || skipToken)
    const [triggerUpdateLead, { status: updateStatus, error: updateError }] = useUpdateLeadMutation()
    const prefetchChains = usePrefetch('getChains')
    const navigate = useNavigate()
    const componentName = formStepDefinition.component
    const currentStepIndex = form.steps.reduce((previousValue, currentValue, currentIndex) => {
        return currentValue.component === componentName ? currentIndex : previousValue
    }, 0)
    React.useEffect(() => {
        if (updateStatus === 'fulfilled') {
            if (currentStepIndex === form.steps.length - 1) {
                navigate('/Agreements')
                return
            }
            navigate('/' + form.steps[currentStepIndex + 1].component)
            if (form.steps.length > currentStepIndex + 2) {
                preloadData(form.steps[currentStepIndex + 2].component)
            }
        }
    }, [updateStatus])

    const handleSubmit = (values: Record<string, any>) => {
        triggerUpdateLead({ token: token, [componentName]: values })
    }

    const disabled = updateStatus === 'pending'

    const preloadData = (componentName: string) => {
        switch (componentName) {
            case 'Chain':
                prefetchChains()
        }
    }

    const renderComponent = () => {
        if (!lead) {
            return
        }
        const props = {
            disabled: disabled,
            values: lead?.values ? (lead.values[componentName] || {}) : {},
            lead: lead,
            onSubmit: handleSubmit
        }
        switch (componentName) {
            case 'CompanyAddress':
                return <CompanyAddress {...props}/>
            case 'Ceo':
                return <Ceo {...props}/>
            case 'TaxNumber':
                return <TaxNumber {...props}/>
            case 'BankAccount':
                return <BankAccount {...props}/>
            case 'CompanyContact':
                return <CompanyContact {...props}/>
            case 'Chain':
                return <Chain {...props}/>
            case 'IataAndCrs':
                return <IataAndCrs {...props}/>
            case 'Comment':
                return <Comment {...props}/>
            case 'Agreements':
                return <Agreements lead={lead} agreements={form.agreements}/>
            default:
                return <Alert severity="error">Missing case for "<strong>{componentName}</strong>" in FormStepRender.tsx</Alert>
        }
    }

    return <>
        {lead ?
            renderComponent()
            : <CircularProgress/>
        }
    </>
}
